import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { LuCircleAlert, LuDownload, LuImageOff, LuMaximize2 } from "react-icons/lu";
import AgentMark from "./AgentMark";
import AuthMedia, { useResolvedUrl } from "./AuthMedia";
import MediaLightbox from "./MediaLightbox";

function cx(...classes) {
    return classes.filter(Boolean).join(" ");
}

const ICON_BUTTON =
    "flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border-primary)] bg-white text-primary-700 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:shadow-[var(--ring)]";

function Avatar() {
    return (
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-[0_0_0_1px_var(--hairline)]">
            <AgentMark className="h-[18px] w-[18px]" />
        </span>
    );
}

/** One generated image as a result card: the picture, then the actions that belong to it. */
function ResultImage({ item, onOpen, fill }) {
    const { url, failed } = useResolvedUrl(item.url);
    const videoRef = useRef(null);
    const isVideo = item.kind === "video";
    const open = () => {
        videoRef.current?.pause();
        onOpen({ url, name: item.name, kind: item.kind });
    };

    const download = () => {
        if (!url) return;
        const link = document.createElement("a");
        link.href = url;
        link.download = item.name || "tryon-image.png";
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    return (
        <div className={`group relative overflow-hidden rounded-2xl shadow-[0_0_0_1px_var(--hairline)] ${fill ? "bg-ground" : "w-fit max-w-full"}`}>
            {failed ? (
                <div className="flex aspect-[4/5] items-center justify-center text-graphite">
                    <LuImageOff size={22} />
                </div>
            ) : !url ? (
                <div className="aspect-[4/5] w-full animate-pulse bg-primary-50" />
            ) : isVideo ? (
                <video
                    ref={videoRef}
                    src={url}
                    controls
                    playsInline
                    className="block h-auto max-h-[30rem] w-auto max-w-sm bg-ground"
                />
            ) : (
                <button
                    type="button"
                    onClick={() => onOpen({ url, name: item.name })}
                    aria-label={`View ${item.name} full screen`}
                    className="block w-full cursor-zoom-in focus:outline-none focus-visible:shadow-[var(--ring)]"
                >
                    <img
                        src={url}
                        alt={item.name}
                        loading="lazy"
                        className={
                            fill
                                ? "mx-auto block max-h-[30rem] w-full object-contain"
                                : "block h-auto w-auto max-h-[30rem] max-w-sm"
                        }
                    />
                </button>
            )}
            {url && (
                <div className="absolute right-3 top-3 flex gap-2 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                    <button type="button" onClick={isVideo ? open : () => onOpen({ url, name: item.name, kind: item.kind })} aria-label="View full screen" title="View full screen" className={ICON_BUTTON}>
                        <LuMaximize2 size={15} />
                    </button>
                    <button type="button" onClick={download} aria-label={isVideo ? "Download video" : "Download image"} title="Download" className={ICON_BUTTON}>
                        <LuDownload size={15} />
                    </button>
                </div>
            )}
        </div>
    );
}

function UserMessage({ message, onOpen }) {
    const media = message.media ?? [];
    return (
        <div className="agent-in flex justify-end">
            <div className="flex w-full flex-col items-end gap-2">
                {media.length > 0 && (
                    <div className="flex w-fit max-w-full flex-wrap justify-end gap-2">
                        {media.map((item) => (
                            <div key={item.id} className="w-fit max-w-full overflow-hidden rounded-2xl leading-none shadow-[0_0_0_1px_var(--hairline)]">
                                <AuthMedia
                                    kind={item.kind}
                                    src={item.url}
                                    alt={item.name}
                                    onOpen={(url) => onOpen({ url, name: item.name, kind: item.kind })}
                                    className={
                                        media.length > 1
                                            ? "block h-auto max-h-48 w-auto max-w-full"
                                            : "block h-auto w-auto max-h-[30rem] max-w-sm"
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
                {message.content && (
                    <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-primary-50 px-4 py-2.5 font-Mulish text-[15px] leading-relaxed text-secondary shadow-[0_0_0_1px_var(--hairline)] sm:max-w-[75%]">
                        {message.content}
                    </p>
                )}
            </div>
        </div>
    );
}

function AssistantMessage({ message, onOpen, onChoose, canChoose }) {
    const media = message.media ?? [];
    const options = message.options ?? [];
    const paid = message.creditsUsed > 0;

    return (
        <div className="agent-in flex gap-3">
            <Avatar />
            <div className="min-w-0 flex-1">
                <p className="font-Mulish text-[13px] font-semibold text-secondary">TryOn AI</p>

                {message.content && (
                    <div className="tryon-agent-md mt-1 max-w-[62ch] font-Mulish text-[15px] leading-[1.7] text-graphite">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                )}

                {media.length > 0 && (
                    <div className={cx("mt-3", media.length > 1 ? "grid max-w-xl grid-cols-2 gap-3" : "w-fit max-w-full")}>
                        {media.map((item) => (
                            <ResultImage key={item.id} item={item} onOpen={onOpen} fill={media.length > 1} />
                        ))}
                    </div>
                )}

                {paid && (
                    <span className="mt-3 inline-flex items-center rounded-full bg-primary-50 px-3 py-1 font-Mulish text-[13px] text-primary-700">
                        {message.creditsUsed} credits used
                    </span>
                )}

                {options.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {options.map((option) => (
                            <button
                                key={option}
                                type="button"
                                disabled={!canChoose}
                                onClick={() => onChoose(option)}
                                className="rounded-full border border-primary-200 bg-white px-3.5 py-1.5 font-Mulish text-sm text-primary-700 transition-colors hover:bg-primary-50 disabled:opacity-50"
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ErrorMessage({ error }) {
    const navigate = useNavigate();
    const needed = Number(String(error).match(/needs\s+(\d+)\s+credits/i)?.[1] || 0);
    const needsCredits = needed > 0 || /credits/i.test(error);
    return (
        <div className="agent-in flex gap-3">
            <Avatar />
            <div className="min-w-0 max-w-[62ch] flex-1 rounded-2xl bg-white px-4 py-3 font-Mulish shadow-[inset_3px_0_0_0_rgb(var(--rgb-accent-700)),0_0_0_1px_var(--hairline)]">
                <div className="flex items-center gap-2 text-primary-700">
                    <LuCircleAlert size={16} />
                    <p className="text-sm font-semibold text-secondary">{needsCredits ? "Not enough credits" : "Something went wrong"}</p>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-graphite">{error}</p>
                {needsCredits && (
                    <button
                        type="button"
                        onClick={() => navigate(needed ? `/credits?buy_credits=true&num_of_credits=${needed}` : "/credits")}
                        className="mt-3 rounded-full bg-primary-700 px-4 py-1.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)] transition-colors hover:bg-primary-800"
                    >
                        Buy credits
                    </button>
                )}
            </div>
        </div>
    );
}

export default function MessageBubble({ message, onChoose, canChoose }) {
    const [preview, setPreview] = useState(null);

    let body;
    if (message.error) body = <ErrorMessage error={message.error} />;
    else if (message.role === "user") body = <UserMessage message={message} onOpen={setPreview} />;
    else body = <AssistantMessage message={message} onOpen={setPreview} onChoose={onChoose} canChoose={canChoose} />;

    return (
        <>
            {body}
            {preview && <MediaLightbox src={preview.url} name={preview.name} kind={preview.kind} onClose={() => setPreview(null)} />}
        </>
    );
}
